import React from 'react';
import { motion } from 'framer-motion';
import { format, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay } from 'date-fns';

export default function CalendarWeekView({ currentDate, appointments, onAppointmentClick, onDateClick }) {
  const weekStart = startOfWeek(currentDate);
  const weekEnd = endOfWeek(currentDate);
  const weekDays = eachDayOfInterval({ start: weekStart, end: weekEnd });

  const hours = Array.from({ length: 24 }, (_, i) => i);

  const getAppointmentsForDayAndHour = (day, hour) => {
    return appointments?.filter(apt => {
      const aptDate = new Date(apt.startTime);
      return isSameDay(aptDate, day) && aptDate?.getHours() === hour;
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      scheduled: 'bg-blue-500',
      confirmed: 'bg-green-500',
      in_progress: 'bg-yellow-500',
      completed: 'bg-gray-500',
      cancelled: 'bg-red-500',
      no_show: 'bg-orange-500'
    };
    return colors?.[status] || 'bg-gray-500';
  };

  const handleTimeSlotClick = (day, hour) => {
    const dateTime = new Date(day);
    dateTime?.setHours(hour, 0, 0, 0);
    onDateClick(dateTime);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.2 }}
      className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
    >
      {/* Day Headers */}
      <div className="grid grid-cols-8 border-b border-gray-200 sticky top-0 bg-white z-10">
        <div className="px-4 py-3 text-center text-sm font-semibold text-gray-700 bg-gray-50 border-r border-gray-200">
          Time
        </div>
        {weekDays?.map((day) => (
          <div
            key={day?.toString()}
            className="px-4 py-3 text-center border-r border-gray-200 bg-gray-50"
          >
            <div className="text-xs text-gray-600">{format(day, 'EEE')}</div>
            <div className="text-lg font-semibold text-gray-900">
              {format(day, 'd')}
            </div>
          </div>
        ))}
      </div>
      {/* Time Grid */}
      <div className="overflow-auto max-h-[calc(100vh-300px)]">
        {hours?.map((hour) => (
          <div key={hour} className="grid grid-cols-8 border-b border-gray-200 min-h-[60px]">
            <div className="px-4 py-2 text-xs text-gray-600 border-r border-gray-200 bg-gray-50">
              {format(new Date()?.setHours(hour, 0, 0, 0), 'h:mm a')}
            </div>
            {weekDays?.map((day) => {
              const dayAppointments = getAppointmentsForDayAndHour(day, hour);
              return (
                <div
                  key={`${day}-${hour}`}
                  onClick={() => handleTimeSlotClick(day, hour)}
                  className="border-r border-gray-200 p-1 cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  {dayAppointments?.map((apt) => (
                    <div
                      key={apt?.id}
                      onClick={(e) => {
                        e?.stopPropagation();
                        onAppointmentClick(apt);
                      }}
                      className={`${getStatusColor(
                        apt?.status
                      )} text-white text-xs p-2 rounded mb-1 cursor-pointer hover:opacity-90 transition-opacity`}
                    >
                      <div className="font-medium truncate">
                        {format(new Date(apt.startTime), 'h:mm a')}
                      </div>
                      <div className="truncate">{apt?.title}</div>
                      {apt?.customer && (
                        <div className="truncate text-xs opacity-90">
                          {apt?.customer?.company_name || apt?.customer?.contact_person}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </motion.div>
  );
}